/**
 * Parse various user-entered date/time formats to a Date object.
 * More flexible than parseTimestamp - designed for direct user input in date pickers.
 *
 * Supports:
 * - ISO 8601 format (2024-12-15T10:00:00Z, 2024-12-15)
 * - MM/DD/YYYY and M/D/YYYY (12/15/2024, 1/5/2024)
 * - MM-DD-YYYY and M-D-YYYY (12-15-2024, 1-5-2024)
 * - "MMM D, YYYY" format (Jan 15, 2024, January 15, 2024)
 * - "MMM D, YYYY at hh:mm a" format (Jan 15, 2024 at 2:30 PM)
 * - "MMM D, YYYY at HH:mm" format (Jan 15, 2024 at 14:30)
 *
 * @param value - The string to parse
 * @returns A Date object if parsing succeeds, null otherwise
 */
export const parseDateInput = (value: string): Date | null => {
  if (!value || !value.trim()) return null;

  const trimmed = value.trim();

  // Try native Date parsing first (handles ISO 8601 and many common formats)
  const nativeDate = new Date(trimmed);
  if (!isNaN(nativeDate.getTime())) {
    return nativeDate;
  }

  // Try MM/DD/YYYY or M/D/YYYY format
  const slashDateMatch = trimmed.match(/^(\d{1,2})\/(\d{1,2})\/(\d{2,4})$/);
  if (slashDateMatch) {
    const month = slashDateMatch[1];
    const day = slashDateMatch[2];
    const year = slashDateMatch[3];
    if (month && day && year) {
      const fullYear = year.length === 2 ? `20${year}` : year;
      const date = new Date(parseInt(fullYear), parseInt(month) - 1, parseInt(day));
      if (!isNaN(date.getTime())) {
        return date;
      }
    }
  }

  // Try MM-DD-YYYY or M-D-YYYY format
  const dashDateMatch = trimmed.match(/^(\d{1,2})-(\d{1,2})-(\d{2,4})$/);
  if (dashDateMatch) {
    const month = dashDateMatch[1];
    const day = dashDateMatch[2];
    const year = dashDateMatch[3];
    if (month && day && year) {
      const fullYear = year.length === 2 ? `20${year}` : year;
      const date = new Date(parseInt(fullYear), parseInt(month) - 1, parseInt(day));
      if (!isNaN(date.getTime())) {
        return date;
      }
    }
  }

  // Try "MMM D, YYYY" or "MMMM D, YYYY" with optional time
  // Examples: "Jan 15, 2024", "January 15, 2024", "Jan 15, 2024 at 2:30 PM"
  const monthNames: Record<string, number> = {
    jan: 0,
    january: 0,
    feb: 1,
    february: 1,
    mar: 2,
    march: 2,
    apr: 3,
    april: 3,
    may: 4,
    jun: 5,
    june: 5,
    jul: 6,
    july: 6,
    aug: 7,
    august: 7,
    sep: 8,
    sept: 8,
    september: 8,
    oct: 9,
    october: 9,
    nov: 10,
    november: 10,
    dec: 11,
    december: 11,
  };

  // Match "MMM D, YYYY" with optional " at hh:mm:ss a" or " at HH:mm:ss"
  const textDateTimeMatch = trimmed.match(
    /^([a-zA-Z]+)\s+(\d{1,2}),?\s+(\d{4})(?:\s+at\s+(\d{1,2}):(\d{2})(?::(\d{2}))?\s*(am|pm|AM|PM)?)?$/i
  );

  if (textDateTimeMatch) {
    const monthStr = textDateTimeMatch[1];
    const day = textDateTimeMatch[2];
    const year = textDateTimeMatch[3];
    const hours = textDateTimeMatch[4];
    const minutes = textDateTimeMatch[5];
    const seconds = textDateTimeMatch[6];
    const ampm = textDateTimeMatch[7];
    if (monthStr && day && year) {
      const monthIndex = monthNames[monthStr.toLowerCase()];

      if (monthIndex !== undefined) {
        let hour = hours ? parseInt(hours) : 0;
        const minute = minutes ? parseInt(minutes) : 0;
        const second = seconds ? parseInt(seconds) : 0;

        // Handle AM/PM conversion
        if (ampm) {
          const isPM = ampm.toLowerCase() === "pm";
          if (isPM && hour !== 12) {
            hour += 12;
          } else if (!isPM && hour === 12) {
            hour = 0;
          }
        }

        const date = new Date(parseInt(year), monthIndex, parseInt(day), hour, minute, second);

        if (!isNaN(date.getTime())) {
          return date;
        }
      }
    }
  }

  // Try parsing time-only additions to existing patterns
  // Handle "M/D/YYYY H:mm a" or "M/D/YYYY HH:mm" formats
  const dateTimeSlashMatch = trimmed.match(
    /^(\d{1,2})\/(\d{1,2})\/(\d{2,4})\s+(\d{1,2}):(\d{2})(?::(\d{2}))?\s*(am|pm|AM|PM)?$/i
  );

  if (dateTimeSlashMatch) {
    const month = dateTimeSlashMatch[1];
    const day = dateTimeSlashMatch[2];
    const year = dateTimeSlashMatch[3];
    const hours = dateTimeSlashMatch[4];
    const minutes = dateTimeSlashMatch[5];
    const seconds = dateTimeSlashMatch[6];
    const ampm = dateTimeSlashMatch[7];
    if (month && day && year && hours && minutes) {
      const fullYear = year.length === 2 ? `20${year}` : year;
      let hour = parseInt(hours);
      const minute = parseInt(minutes);
      const second = seconds ? parseInt(seconds) : 0;

      if (ampm) {
        const isPM = ampm.toLowerCase() === "pm";
        if (isPM && hour !== 12) {
          hour += 12;
        } else if (!isPM && hour === 12) {
          hour = 0;
        }
      }

      const date = new Date(parseInt(fullYear), parseInt(month) - 1, parseInt(day), hour, minute, second);

      if (!isNaN(date.getTime())) {
        return date;
      }
    }
  }

  return null;
};
